'use client';
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { Article } from '@/lib/types';
import { cn, parseDate, isVideoUrl } from '@/lib/utils';
import { getArticleUrl } from '@/lib/articles';
import { ShareButton } from '@/components/share-button';
import { VideoPreview } from './cards/video-preview';

interface ArticleCardProps {
  article: Article;
  priority?: boolean;
  className?: string;
  layout?: 'default' | 'side' | 'side-mobile' | 'overlay' | 'overlay-side' | 'large' | 'side-horizontal' | 'horizontal-list' | 'horizontal-list-left' | 'atlas-horizontal';
  mobileLayout?: string;
  hideLede?: boolean;
  hideCategory?: boolean;
  hideImage?: boolean;
  hideImageDesktop?: boolean;
}

function formatUtcDate(dateVal: any) {
  const date = parseDate(dateVal);
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function ArticleCard({ 
  article, priority = false, className, layout = 'default', mobileLayout, hideLede, hideImage, hideImageDesktop 
}: ArticleCardProps) {
  if (!article) return null;
  const articleUrl = getArticleUrl(article);
  const image = article.images?.[0];
  
  const DateOnlyRow = (isOverlay?: boolean) => (
    <div className={cn(
      "flex items-center text-sm md:text-[10px] font-bold tracking-widest transition-colors relative z-30",
      isOverlay ? "text-white/60" : "text-muted-foreground"
    )}>
      <span className="whitespace-nowrap">{formatUtcDate(article.publishDate)}</span>
    </div>
  );

  const SponsoredTag = (isOverlay?: boolean) => {
    if (article.article !== 'Sponsored') return null;
    return (
      <span className={cn(
        "px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border shadow-sm inline-block mb-1.5",
        isOverlay 
          ? "bg-white text-black border-white" 
          : "bg-primary text-white border-primary"
      )}>
        Sponsored
      </span>
    );
  };

  const renderMedia = (priorityFlag?: boolean, sizesHint?: string) => {
    if (hideImage) return null;
    const videoSource = (article.type === 'video' && article.video?.contentUrl) ? article.video.contentUrl : (image?.src && isVideoUrl(image.src) ? image.src : null);
    if (videoSource) {
      const poster = article.video?.thumbnail || (image?.src && !isVideoUrl(image.src) ? image.src : undefined);
      return (
        <div className={cn("absolute inset-0", hideImageDesktop && "md:hidden")}>
          <VideoPreview url={videoSource} poster={poster} className="absolute inset-0" priority={priorityFlag} />
        </div>
      );
    }
    return (image?.src) ? (
      <Image src={image.src} alt="" fill sizes={sizesHint || "100vw"} className={cn("object-cover transition-transform duration-700 group-hover:scale-105", hideImageDesktop && "md:hidden")} priority={priorityFlag} aria-hidden="true" />
    ) : null;
  };

  const isOverlayDesktop = layout === 'overlay' || layout === 'overlay-side';
  const isHorizontalDesktop = layout === 'horizontal-list' || layout === 'horizontal-list-left' || layout === 'side-horizontal' || layout === 'atlas-horizontal';
  const isDefaultDesktop = !isOverlayDesktop && !isHorizontalDesktop;

  const isOverlayMobile = mobileLayout === 'overlay';
  const isSideMobile = mobileLayout === 'side-mobile' || mobileLayout === 'horizontal-list';
  const isDefaultMobile = mobileLayout === 'default' || (!mobileLayout && !isOverlayMobile && !isSideMobile);

  return (
    <div className={cn("group relative flex flex-col transition-all duration-300 h-full", className)}>
      {/* MOBILE: Standard card UI with background */}
      <div className="md:hidden h-full flex flex-col">
        {isOverlayMobile && (
          <div className="relative flex flex-col overflow-hidden bg-[hsl(var(--mobile-card))] rounded-2xl mx-4 mb-1 min-h-[400px] h-full">
            <div className="absolute inset-0 z-0">
              {renderMedia(priority, "100vw")}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
            </div>
            <Link href={articleUrl} className="absolute inset-0 z-10" prefetch={false} />
            <div className="absolute inset-0 flex flex-col justify-end pt-10 px-5 pb-4 text-white z-20 pointer-events-none">
              <SponsoredTag isOverlay />
              <h3 className="mb-2 text-white font-semibold text-xl leading-tight line-clamp-3 font-headline pointer-events-auto">{article.headline}</h3>
              <p className="text-xs text-white/90 line-clamp-2 mb-4 font-medium leading-snug">{article.subheadline || article.lede}</p>
              <div className="flex items-center justify-between mt-1 pointer-events-auto">
                {DateOnlyRow(true)}
                <ShareButton article={article} className="h-10 w-10 text-white hover:bg-white/10" />
              </div>
            </div>
          </div>
        )}
        {isSideMobile && (
          <div className="flex flex-col bg-[hsl(var(--mobile-card))] text-foreground rounded-2xl mx-4 mb-1 pt-4 pb-4 px-0">
            <div className="flex justify-between gap-4 items-center min-w-0 px-4">
              <div className="flex-1 min-w-0">
                <SponsoredTag />
                <Link href={articleUrl} className="block hover:text-primary transition-colors" prefetch={false}>
                  <h3 className="text-base font-semibold text-current mb-2 line-clamp-4 leading-tight font-headline">{article.headline}</h3>
                </Link>
              </div>
              {!hideImage && (
                <div className="relative shrink-0 overflow-hidden rounded-xl w-[85px] h-[85px] bg-muted shadow-sm">
                  <Link href={articleUrl} className="block w-full h-full relative z-10" prefetch={false}>{renderMedia(priority, "85px")}</Link>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-1 pt-2 min-w-0 px-4">
              {DateOnlyRow()}
              <ShareButton article={article} className="h-8 w-8 text-current opacity-60 ml-auto" />
            </div>
          </div>
        )}
        {isDefaultMobile && (
          <div className="flex flex-col bg-[hsl(var(--mobile-card))] text-foreground rounded-2xl mx-4 mb-1 overflow-hidden h-full">
            {!hideImage && (
              <Link href={articleUrl} className="block w-full relative z-10" prefetch={false}>
                <div className="relative overflow-hidden aspect-video bg-muted">{renderMedia(priority, "100vw")}</div>
              </Link>
            )}
            <div className="pt-4 pb-4 px-4 flex flex-col flex-1 min-w-0">
              <SponsoredTag />
              <Link href={articleUrl} className="block hover:text-primary transition-colors" prefetch={false}>
                <h3 className={cn("leading-tight mb-2 font-semibold text-lg text-current line-clamp-4 font-headline")}>{article.headline}</h3>
              </Link>
              <p className="text-xs opacity-70 line-clamp-2 leading-snug font-medium mb-4">{article.subheadline || article.lede}</p>
              <div className="flex items-center justify-between mt-auto pt-3">
                {DateOnlyRow()}
                <ShareButton article={article} className="h-8 w-8 text-current opacity-60 ml-auto" />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* DESKTOP: Transparent cards to blend into section backgrounds */}
      <div className="hidden md:flex flex-col h-full w-full">
        {isOverlayDesktop && (
          <div className="relative flex-1 flex flex-col overflow-hidden rounded-2xl">
            <div className="absolute inset-0 z-0">
              {renderMedia(priority, "800px")}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
            </div>
            <Link href={articleUrl} className="absolute inset-0 z-10" prefetch={false} />
            <div className="absolute inset-0 flex flex-col justify-end pt-10 px-8 pb-6 text-white z-20 pointer-events-none">
              <SponsoredTag isOverlay />
              <Link href={articleUrl} className="pointer-events-auto block hover:text-primary transition-colors" prefetch={false}>
                <h3 className={cn("mb-3 text-white font-bold leading-tight font-headline md:text-xl")}>{article.headline}</h3>
              </Link>
              <p className="text-sm text-white/90 line-clamp-2 mb-6 font-medium leading-relaxed pointer-events-auto w-full break-words">{article.subheadline || article.lede}</p>
              <div className="flex items-center justify-between gap-3 mt-1 pointer-events-auto">
                {DateOnlyRow(true)}
                <ShareButton article={article} className="h-10 w-10 text-white hover:bg-white/10" />
              </div>
            </div>
          </div>
        )}
        {isHorizontalDesktop && (
          <div className={cn("flex flex-col h-full w-full", className)}>
            <div className={cn("flex justify-between gap-6 items-start min-w-0 px-0", layout === 'horizontal-list-left' && "md:flex-row-reverse")}>
              <div className="flex-1 min-w-0">
                <SponsoredTag />
                <Link href={articleUrl} className="block hover:text-primary transition-colors" prefetch={false}>
                  <h3 className="font-semibold text-current mb-1.5 leading-snug font-headline line-clamp-3 break-words w-full md:text-sm">
                    {article.headline}
                  </h3>
                </Link>
                <p className="text-xs opacity-50 line-clamp-2 leading-relaxed mb-1">{article.subheadline || article.lede}</p>
              </div>
              {!hideImage && (
                <div className="relative shrink-0 overflow-hidden rounded-xl bg-muted shadow-sm md:w-[85px] md:h-[85px] mt-1">
                  <Link href={articleUrl} className="block w-full h-full relative z-10" prefetch={false}>{renderMedia(priority, "300px")}</Link>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-auto pt-3 px-0">
              {DateOnlyRow()}
              <ShareButton article={article} className="h-8 w-8 text-foreground/70 hover:text-primary transition-colors ml-auto" />
            </div>
          </div>
        )}
        {isDefaultDesktop && (
          <div className="flex flex-col h-full w-full">
            {!hideImage && (
              <Link href={articleUrl} className={cn("block w-full relative z-10 mb-4", hideImageDesktop && "md:hidden")} prefetch={false}>
                <div className="relative overflow-hidden aspect-video bg-muted rounded-xl shadow-sm">{renderMedia(priority, "400px")}</div>
              </Link>
            )}
            <div className="pt-0 pb-1 px-0 flex flex-col flex-1 min-w-0">
              <SponsoredTag />
              <Link href={articleUrl} className="block hover:text-primary transition-colors" prefetch={false}>
                <h3 className={cn("leading-tight mb-2 font-semibold text-current line-clamp-3 break-words w-full font-headline md:text-sm")}>
                  {article.headline}
                </h3>
              </Link>
              <p className="text-xs opacity-70 line-clamp-2 leading-relaxed font-medium mb-4">{article.subheadline || article.lede}</p>
              <div className="flex items-center justify-between mt-auto pt-3">
                {DateOnlyRow()}
                <ShareButton article={article} className="h-8 w-8 text-foreground/70 hover:text-primary transition-colors ml-auto" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
